import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  type CommitteeMember,
  type CommitteeMemberType,
  getCommittee,
  getCommitteeMemberImageUrl,
  deleteCommitteeMember,
} from '../../features/committee';
import CommitteeMemberModal from '../../components/dashboard/CommitteeMemberModal';
import {
  DashboardPageHeader,
  DashboardStatCard,
  DeleteConfirmModal,
} from '../../shared/ui';
import { getUserFriendlyErrorMessage, logErrorInDev } from '../../shared/lib';
import './CommitteePage.css';

const MEMBER_TYPE_OPTIONS: CommitteeMemberType[] = [
  'Committee',
  'Trustee',
  'Imaamat',
  'Admin',
  'Staff',
];

const TYPE_META: Record<CommitteeMemberType, { icon: string; badgeClass: string }> = {
  Committee: { icon: 'bi bi-people-fill', badgeClass: '' },
  Trustee: { icon: 'bi bi-shield-check', badgeClass: 'committee-mgmt-card__type--trustee' },
  Imaamat: { icon: 'bi bi-moon-stars', badgeClass: 'committee-mgmt-card__type--imaamat' },
  Admin: { icon: 'bi bi-gear', badgeClass: 'committee-mgmt-card__type--admin' },
  Staff: { icon: 'bi bi-person-workspace', badgeClass: 'committee-mgmt-card__type--staff' },
};

const initialsFor = (member: CommitteeMember): string => {
  const first = member.firstName?.trim()?.[0] ?? '';
  const last = member.lastName?.trim()?.[0] ?? '';
  const value = `${first}${last}`.toUpperCase();
  return value || '?';
};

const fullName = (m: CommitteeMember) => `${m.firstName} ${m.lastName}`.trim();

const CommitteePage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [members, setMembers] = useState<CommitteeMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState<CommitteeMember | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<CommitteeMember | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [filterType, setFilterType] = useState<CommitteeMemberType | ''>('');

  const canView = !!profile && can(profile, 'committee', 'view');
  const canEdit = !!profile && can(profile, 'committee', 'edit');
  const canDelete = !!profile && can(profile, 'committee', 'delete');

  const loadCommittee = async () => {
    setIsLoading(true);
    try {
      const data = await getCommittee();
      setMembers(data);
    } catch (err) {
      logErrorInDev('Failed to load committee', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to load committee members.'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (canView) void loadCommittee();
  }, [canView]);

  const typeCounts = useMemo(() => {
    const counts = Object.fromEntries(MEMBER_TYPE_OPTIONS.map((t) => [t, 0])) as Record<
      CommitteeMemberType,
      number
    >;
    members.forEach((m) => {
      counts[m.memberType] = (counts[m.memberType] ?? 0) + 1;
    });
    return counts;
  }, [members]);

  const linkedCount = useMemo(
    () => members.filter((m) => Boolean(m.userId || m.userEmail)).length,
    [members]
  );

  const filteredMembers = useMemo(() => {
    const term = searchName.trim().toLowerCase();
    return members
      .filter((m) => {
        const matchName =
          !term ||
          `${m.firstName} ${m.lastName}`.toLowerCase().includes(term) ||
          (m.position ?? '').toLowerCase().includes(term) ||
          (m.description ?? '').toLowerCase().includes(term);
        const matchType = !filterType || m.memberType === filterType;
        return matchName && matchType;
      })
      .sort((a, b) => {
        const order = a.displayOrder - b.displayOrder;
        if (order !== 0) return order;
        return fullName(a).localeCompare(fullName(b));
      });
  }, [members, searchName, filterType]);

  const groupedMembers = useMemo(() => {
    return MEMBER_TYPE_OPTIONS.map((type) => ({
      type,
      members: filteredMembers.filter((m) => m.memberType === type),
    })).filter((group) => group.members.length > 0);
  }, [filteredMembers]);

  const handleSaved = () => {
    showToast('Committee member saved.');
    void loadCommittee();
    setShowModal(false);
    setEditingMember(null);
  };

  const handleEdit = (member: CommitteeMember) => {
    setEditingMember(member);
    setShowModal(true);
  };

  const handleDeleteClick = (member: CommitteeMember) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;
    setIsDeleting(true);
    try {
      await deleteCommitteeMember(memberToDelete.id);
      setMembers((prev) => prev.filter((m) => m.id !== memberToDelete.id));
      setShowDeleteModal(false);
      setMemberToDelete(null);
      showToast('Committee member removed.');
    } catch (err) {
      logErrorInDev('Failed to delete committee member', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to delete committee member.'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-shield-exclamation me-2" />
            <strong>Access Restricted:</strong> You do not have permission to view the committee list.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 committee-mgmt-page">
      <DashboardPageHeader
        title="Committee Management"
        description="Curate the people who appear on the public site — committee, trustees, imaamat, and staff."
        icon="bi bi-people-fill"
        actions={
          canEdit ? (
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={() => {
                setEditingMember(null);
                setShowModal(true);
              }}
            >
              <i className="bi bi-plus-circle" aria-hidden />
              Add Member
            </button>
          ) : null
        }
      >
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
            <span className="input-group-text bg-light">
              <i className="bi bi-search text-muted" aria-hidden />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search name, role, or note…"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              aria-label="Search members"
            />
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              setSearchName('');
              setFilterType('');
            }}
          >
            <i className="bi bi-arrow-counterclockwise me-1" aria-hidden />
            Reset
          </button>
          <span className="badge rounded-pill text-bg-light border ms-md-auto">
            Showing {filteredMembers.length} of {members.length}
          </span>
        </div>
      </DashboardPageHeader>

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <DashboardStatCard
            variant="panel"
            tone="primary"
            title="Total members"
            value={members.length}
            icon="bi bi-people"
          />
        </div>
        <div className="col-6 col-lg-3">
          <DashboardStatCard
            variant="panel"
            tone="success"
            title="Committee"
            value={typeCounts.Committee}
            icon="bi bi-people-fill"
          />
        </div>
        <div className="col-6 col-lg-3">
          <DashboardStatCard
            variant="panel"
            tone="warning"
            title="Trustees"
            value={typeCounts.Trustee}
            icon="bi bi-shield-check"
          />
        </div>
        <div className="col-6 col-lg-3">
          <DashboardStatCard
            variant="panel"
            tone="info"
            title="Linked users"
            value={linkedCount}
            icon="bi bi-link-45deg"
          />
        </div>
      </div>

      <div className="committee-mgmt-chips mb-4" role="group" aria-label="Filter by member type">
        <button
          type="button"
          className={`committee-mgmt-chip${!filterType ? ' is-active' : ''}`}
          onClick={() => setFilterType('')}
        >
          All types
          <span className="committee-mgmt-chip__count">{members.length}</span>
        </button>
        {MEMBER_TYPE_OPTIONS.map((type) => (
          <button
            key={type}
            type="button"
            className={`committee-mgmt-chip${filterType === type ? ' is-active' : ''}`}
            onClick={() => setFilterType((prev) => (prev === type ? '' : type))}
          >
            <i className={TYPE_META[type].icon} aria-hidden />
            {type}
            <span className="committee-mgmt-chip__count">{typeCounts[type]}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="row g-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4 col-xl-3">
              <div className="committee-mgmt-skeleton" aria-hidden />
            </div>
          ))}
          <span className="visually-hidden">Loading committee members…</span>
        </div>
      ) : members.length === 0 ? (
        <div className="committee-mgmt-empty text-center py-5">
          <div className="committee-mgmt-empty__icon" aria-hidden>
            <i className="bi bi-people" />
          </div>
          <h3 className="h5 mt-3 mb-2">No committee members yet</h3>
          <p className="text-muted mb-0">Add the first member to populate the public listings.</p>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="committee-mgmt-empty text-center py-5">
          <div className="committee-mgmt-empty__icon" aria-hidden>
            <i className="bi bi-funnel" />
          </div>
          <h3 className="h5 mt-3 mb-2">No matches</h3>
          <p className="text-muted mb-0">Try a different name, role, or type filter.</p>
        </div>
      ) : (
        groupedMembers.map((group) => {
          const meta = TYPE_META[group.type];
          return (
            <section key={group.type} className="committee-mgmt-section">
              <div className="committee-mgmt-section__head">
                <span className="committee-mgmt-section__icon" aria-hidden>
                  <i className={meta.icon} />
                </span>
                <h2 className="committee-mgmt-section__title">{group.type}</h2>
                <span className="committee-mgmt-section__meta">
                  {group.members.length} {group.members.length === 1 ? 'member' : 'members'}
                </span>
              </div>
              <div className="row g-3">
                {group.members.map((member) => {
                  const imageUrl = getCommitteeMemberImageUrl(member);
                  return (
                    <div key={member.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                      <article className="committee-mgmt-card">
                        <div className="committee-mgmt-card__body">
                          <div className="committee-mgmt-card__avatar" aria-hidden>
                            {imageUrl ? (
                              <img src={imageUrl} alt="" loading="lazy" />
                            ) : (
                              <span className="committee-mgmt-card__initials">
                                {initialsFor(member)}
                              </span>
                            )}
                          </div>
                          <h3 className="committee-mgmt-card__name">{fullName(member)}</h3>
                          {member.position ? (
                            <p className="committee-mgmt-card__position">{member.position}</p>
                          ) : null}
                          <span className={`committee-mgmt-card__type ${meta.badgeClass}`}>
                            {group.type}
                          </span>
                          {member.description ? (
                            <p className="committee-mgmt-card__description">{member.description}</p>
                          ) : null}
                          {member.userEmail ? (
                            <div className="committee-mgmt-card__link" title="Linked user">
                              <i className="bi bi-person-check" aria-hidden />
                              <span>{member.userEmail}</span>
                            </div>
                          ) : null}
                        </div>
                        {(canEdit || canDelete) && (
                          <div className="committee-mgmt-card__actions">
                            {canEdit && (
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleEdit(member)}
                              >
                                <i className="bi bi-pencil me-1" aria-hidden />
                                Edit
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDeleteClick(member)}
                              >
                                <i className="bi bi-trash me-1" aria-hidden />
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </article>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      {showModal && (
        <CommitteeMemberModal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setEditingMember(null);
          }}
          onSaved={handleSaved}
          member={editingMember}
        />
      )}
      {showDeleteModal && memberToDelete && (
        <DeleteConfirmModal
          show={showDeleteModal}
          onHide={cancelDelete}
          onConfirm={confirmDelete}
          itemName={fullName(memberToDelete)}
          itemType="committee member"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default CommitteePage;
